// Documentação das bibliotecas utilitárias do sistema Altus Food

export interface LibFunction {
  name: string;
  signature: string;
  description: string;
}

export interface LibDoc {
  name: string;
  path: string;
  description: string;
  category: string;
  functions: LibFunction[];
}

export const LIB_CATEGORIES = [
  { id: "offline", name: "Offline/Cache", icon: "WifiOff" },
  { id: "printing", name: "Impressão", icon: "Printer" },
  { id: "stock", name: "Estoque", icon: "Warehouse" },
  { id: "fiscal", name: "Fiscal", icon: "Receipt" },
  { id: "utils", name: "Utilitários", icon: "Wrench" },
  { id: "realtime", name: "Realtime", icon: "Radio" },
  { id: "export", name: "Exportação", icon: "Download" },
];

export const LIBS_DOCUMENTATION: LibDoc[] = [
  // ========== OFFLINE/CACHE ==========
  {
    name: "offline-storage",
    path: "src/lib/offline-storage.ts",
    description: "Gerenciamento de IndexedDB para operações offline. Armazena pedidos, itens e fechamentos pendentes.",
    category: "offline",
    functions: [
      { name: "initDB", signature: "() => Promise<IDBDatabase>", description: "Inicializa/abre o banco IndexedDB" },
      { name: "savePendingOrder", signature: "(order: PendingOrder) => Promise<void>", description: "Salva pedido pendente" },
      { name: "getPendingOrders", signature: "(restaurantId: string) => Promise<PendingOrder[]>", description: "Lista pedidos pendentes" },
      { name: "deletePendingOrder", signature: "(id: string) => Promise<void>", description: "Remove pedido pendente" },
      { name: "savePendingItem", signature: "(item: PendingItem) => Promise<void>", description: "Salva item pendente" },
      { name: "getPendingItems", signature: "(restaurantId: string) => Promise<PendingItem[]>", description: "Lista itens pendentes" },
      { name: "savePendingClosure", signature: "(closure: PendingClosure) => Promise<void>", description: "Salva fechamento pendente" },
      { name: "getPendingClosures", signature: "(restaurantId: string) => Promise<PendingClosure[]>", description: "Lista fechamentos" },
      { name: "hasPendingOperationsForTable", signature: "(tableId: string) => Promise<boolean>", description: "Verifica operações pendentes" },
      { name: "wasTableClosedOffline", signature: "(tableId: string) => Promise<boolean>", description: "Verifica se mesa foi fechada offline" },
    ],
  },
  {
    name: "sqlite-local",
    path: "src/lib/sqlite-local.ts",
    description: "SQLite WebAssembly para cache local de consumos ativos. Permite operação 100% offline.",
    category: "offline",
    functions: [
      { name: "initSQLite", signature: "() => Promise<Database>", description: "Inicializa SQLite WASM" },
      { name: "createTables", signature: "(db: Database) => void", description: "Cria tabelas de cache" },
      { name: "upsertActiveConsumption", signature: "(entityId, entityType, orderId, items) => void", description: "Salva/atualiza consumo" },
      { name: "getActiveConsumption", signature: "(entityId: string) => Consumption | null", description: "Busca consumo ativo" },
      { name: "deleteActiveConsumption", signature: "(entityId: string) => void", description: "Remove consumo" },
      { name: "getAllActiveConsumptions", signature: "() => Consumption[]", description: "Lista todos os consumos" },
      { name: "updateTableStatus", signature: "(tableId, status) => void", description: "Atualiza status da mesa" },
      { name: "getTableStatus", signature: "(tableId: string) => string", description: "Busca status da mesa" },
    ],
  },
  {
    name: "pdv-cache",
    path: "src/lib/pdv-cache.ts",
    description: "Cache de produtos e categorias para o PDV. Reduz chamadas à API.",
    category: "offline",
    functions: [
      { name: "cacheProducts", signature: "(products: Product[]) => void", description: "Armazena produtos no cache" },
      { name: "getCachedProducts", signature: "() => Product[]", description: "Recupera produtos do cache" },
      { name: "cacheCategories", signature: "(categories: Category[]) => void", description: "Armazena categorias" },
      { name: "getCachedCategories", signature: "() => Category[]", description: "Recupera categorias" },
      { name: "clearCache", signature: "() => void", description: "Limpa todo o cache" },
      { name: "isCacheValid", signature: "(maxAgeMs: number) => boolean", description: "Verifica validade do cache" },
    ],
  },

  // ========== IMPRESSÃO ==========
  {
    name: "order-printing",
    path: "src/lib/order-printing.ts",
    description: "Formatação de cupons de pedido para impressoras térmicas.",
    category: "printing",
    functions: [
      { name: "formatOrderReceipt", signature: "(order, items, restaurant) => string", description: "Formata cupom de pedido" },
      { name: "formatKitchenTicket", signature: "(items, tableNumber, notes) => string", description: "Formata comanda cozinha" },
      { name: "formatPartialPayment", signature: "(payment, order) => string", description: "Formata recibo parcial" },
      { name: "formatClosingReceipt", signature: "(order, payments) => string", description: "Formata cupom fechamento" },
    ],
  },
  {
    name: "cash-printing",
    path: "src/lib/cash-printing.ts",
    description: "Impressão de relatórios de caixa.",
    category: "printing",
    functions: [
      { name: "formatCashRegisterReport", signature: "(cashRegister, movements) => string", description: "Formata relatório de caixa" },
      { name: "formatCashCountReport", signature: "(countDetails, totals) => string", description: "Formata contagem" },
    ],
  },
  {
    name: "cancellation-printing",
    path: "src/lib/cancellation-printing.ts",
    description: "Impressão de comprovantes de cancelamento.",
    category: "printing",
    functions: [
      { name: "formatCancellationReceipt", signature: "(item, reason, cancelledBy) => string", description: "Formata cupom de cancelamento" },
    ],
  },
  {
    name: "partial-payment-printing",
    path: "src/lib/partial-payment-printing.ts",
    description: "Impressão de pagamentos parciais.",
    category: "printing",
    functions: [
      { name: "formatPartialPaymentReceipt", signature: "(payment, order, remaining) => string", description: "Formata recibo" },
    ],
  },
  {
    name: "public-order-printing",
    path: "src/lib/public-order-printing.ts",
    description: "Impressão de pedidos do menu público (QR Code).",
    category: "printing",
    functions: [
      { name: "formatPublicOrderTicket", signature: "(order, tableNumber) => string", description: "Formata pedido público" },
    ],
  },
  {
    name: "print-template-formatting",
    path: "src/lib/print-template-formatting.ts",
    description: "Aplicação de templates customizáveis na impressão.",
    category: "printing",
    functions: [
      { name: "applyTemplate", signature: "(template, data) => string", description: "Aplica template" },
      { name: "parseVariables", signature: "(text, variables) => string", description: "Substitui variáveis" },
    ],
  },
  {
    name: "print-template-test",
    path: "src/lib/print-template-test.ts",
    description: "Testes de templates de impressão.",
    category: "printing",
    functions: [
      { name: "generateTestReceipt", signature: "(template) => string", description: "Gera cupom de teste" },
    ],
  },
  {
    name: "qz-tray",
    path: "src/lib/qz-tray.ts",
    description: "Integração com QZ Tray para impressão direta em impressoras locais.",
    category: "printing",
    functions: [
      { name: "connect", signature: "() => Promise<void>", description: "Conecta ao QZ Tray" },
      { name: "disconnect", signature: "() => void", description: "Desconecta" },
      { name: "isConnected", signature: "() => boolean", description: "Verifica conexão" },
      { name: "getPrinters", signature: "() => Promise<string[]>", description: "Lista impressoras do SO" },
      { name: "print", signature: "(printer, data, options?) => Promise<void>", description: "Envia para impressora" },
      { name: "signRequest", signature: "(request) => Promise<string>", description: "Assina requisição" },
    ],
  },
  {
    name: "centralized-print-queue",
    path: "src/lib/centralized-print-queue.ts",
    description: "Fila centralizada de impressão com retry automático e persistência.",
    category: "printing",
    functions: [
      { name: "addToQueue", signature: "(job: PrintJob) => void", description: "Adiciona job à fila" },
      { name: "processQueue", signature: "() => Promise<void>", description: "Processa fila" },
      { name: "getQueueStatus", signature: "() => QueueStatus", description: "Status da fila" },
      { name: "retryJob", signature: "(jobId: string) => Promise<void>", description: "Reprocessa job" },
      { name: "clearFailedJobs", signature: "() => void", description: "Limpa jobs com falha" },
    ],
  },
  {
    name: "print-queue",
    path: "src/lib/print-queue.ts",
    description: "Fila de impressão simples (legacy).",
    category: "printing",
    functions: [
      { name: "enqueue", signature: "(data: PrintData) => void", description: "Adiciona à fila" },
      { name: "dequeue", signature: "() => PrintData | null", description: "Remove da fila" },
    ],
  },
  {
    name: "print-routing",
    path: "src/lib/print-routing.ts",
    description: "Roteamento de impressão por local de produção.",
    category: "printing",
    functions: [
      { name: "routeToPrinter", signature: "(item, locations) => Printer", description: "Determina impressora" },
      { name: "groupByPrinter", signature: "(items, locations) => Map<Printer, Item[]>", description: "Agrupa por impressora" },
    ],
  },
  {
    name: "print-log",
    path: "src/lib/print-log.ts",
    description: "Log de impressões para debug.",
    category: "printing",
    functions: [
      { name: "logPrintJob", signature: "(job, status, error?) => void", description: "Registra job" },
      { name: "getPrintLogs", signature: "(limit?) => PrintLog[]", description: "Busca logs" },
    ],
  },

  // ========== ESTOQUE ==========
  {
    name: "stock-deduction",
    path: "src/lib/stock-deduction.ts",
    description: "Baixa automática de estoque quando itens são vendidos.",
    category: "stock",
    functions: [
      { name: "deductStock", signature: "(orderItems: OrderItem[]) => Promise<void>", description: "Baixa estoque dos itens vendidos" },
      { name: "calculateDeduction", signature: "(product, quantity) => Deduction[]", description: "Calcula ingredientes a baixar" },
    ],
  },
  {
    name: "stock-reversal",
    path: "src/lib/stock-reversal.ts",
    description: "Estorno de estoque quando itens são cancelados.",
    category: "stock",
    functions: [
      { name: "reverseStock", signature: "(cancelledItem: OrderItem) => Promise<void>", description: "Estorna estoque do item" },
    ],
  },
  {
    name: "weighted-average-cost",
    path: "src/lib/weighted-average-cost.ts",
    description: "Cálculo de custo médio ponderado para estoque.",
    category: "stock",
    functions: [
      { name: "calculateWAC", signature: "(currentStock, currentCost, newQty, newCost) => number", description: "Calcula novo custo médio" },
      { name: "updateIngredientCost", signature: "(ingredientId, newEntry) => Promise<void>", description: "Atualiza custo do ingrediente" },
    ],
  },

  // ========== FISCAL ==========
  {
    name: "nfce-ini-builder",
    path: "src/lib/nfce-ini-builder.ts",
    description: "Gera arquivo INI no formato esperado pelo ACBrMonitor para emissão de NFC-e.",
    category: "fiscal",
    functions: [
      { name: "buildNfceIni", signature: "(order, restaurant, fiscalConfig) => string", description: "Gera conteúdo do INI" },
      { name: "formatCurrency", signature: "(value: number) => string", description: "Formata valor monetário" },
      { name: "formatCPF", signature: "(cpf: string) => string", description: "Formata CPF" },
    ],
  },
  {
    name: "fiscal-constants",
    path: "src/lib/fiscal-constants.ts",
    description: "Constantes fiscais (CST, CFOP, etc).",
    category: "fiscal",
    functions: [
      { name: "CST_ICMS", signature: "Record<string, string>", description: "Códigos CST ICMS" },
      { name: "CST_PIS_COFINS", signature: "Record<string, string>", description: "Códigos CST PIS/COFINS" },
      { name: "CFOP_CODES", signature: "Record<string, string>", description: "Códigos CFOP" },
      { name: "ORIGEM_MERCADORIA", signature: "Record<string, string>", description: "Origem da mercadoria" },
    ],
  },

  // ========== UTILITÁRIOS ==========
  {
    name: "utils",
    path: "src/lib/utils.ts",
    description: "Funções utilitárias gerais.",
    category: "utils",
    functions: [
      { name: "cn", signature: "(...inputs: ClassValue[]) => string", description: "Merge de classes Tailwind" },
      { name: "normalizeTableNumber", signature: "(number: string) => string", description: "Normaliza número de mesa" },
      { name: "sortByNumberField", signature: "(items: T[]) => T[]", description: "Ordena por campo numérico" },
    ],
  },
  {
    name: "currency-cents",
    path: "src/lib/currency-cents.ts",
    description: "Conversão de valores monetários (reais ↔ centavos).",
    category: "utils",
    functions: [
      { name: "toCents", signature: "(reais: number) => number", description: "Converte reais para centavos" },
      { name: "toReais", signature: "(cents: number) => number", description: "Converte centavos para reais" },
      { name: "formatCurrency", signature: "(value: number) => string", description: "Formata para exibição (R$ X,XX)" },
    ],
  },
  {
    name: "validation-schemas",
    path: "src/lib/validation-schemas.ts",
    description: "Schemas Zod para validação de formulários.",
    category: "utils",
    functions: [
      { name: "productSchema", signature: "z.ZodObject", description: "Schema de produto" },
      { name: "categorySchema", signature: "z.ZodObject", description: "Schema de categoria" },
      { name: "expenseSchema", signature: "z.ZodObject", description: "Schema de despesa" },
      { name: "userSchema", signature: "z.ZodObject", description: "Schema de usuário" },
    ],
  },
  {
    name: "error-dedup",
    path: "src/lib/error-dedup.ts",
    description: "Deduplicação de erros repetidos no console.",
    category: "utils",
    functions: [
      { name: "shouldLogError", signature: "(error: Error) => boolean", description: "Verifica se deve logar" },
      { name: "getErrorFingerprint", signature: "(error: Error) => string", description: "Gera fingerprint do erro" },
    ],
  },
  {
    name: "error-messages",
    path: "src/lib/error-messages.ts",
    description: "Mensagens de erro amigáveis.",
    category: "utils",
    functions: [
      { name: "getErrorMessage", signature: "(error: unknown) => string", description: "Extrai mensagem do erro" },
      { name: "translateSupabaseError", signature: "(code: string) => string", description: "Traduz erro do Supabase" },
    ],
  },
  {
    name: "greetings",
    path: "src/lib/greetings.ts",
    description: "Saudações baseadas no horário.",
    category: "utils",
    functions: [
      { name: "getGreeting", signature: "() => string", description: "Retorna saudação (Bom dia, Boa tarde, etc)" },
    ],
  },
  {
    name: "logger",
    path: "src/lib/logger.ts",
    description: "Logger estruturado para debug.",
    category: "utils",
    functions: [
      { name: "log", signature: "(level, message, data?) => void", description: "Log genérico" },
      { name: "debug", signature: "(message, data?) => void", description: "Log de debug" },
      { name: "error", signature: "(message, error?) => void", description: "Log de erro" },
    ],
  },
  {
    name: "notification-sound",
    path: "src/lib/notification-sound.ts",
    description: "Reprodução de sons de notificação.",
    category: "utils",
    functions: [
      { name: "playNotificationSound", signature: "(type: 'order' | 'call') => void", description: "Toca som de notificação" },
    ],
  },

  // ========== REALTIME ==========
  {
    name: "supabase-channel",
    path: "src/lib/supabase-channel.ts",
    description: "Gestão de canais Realtime do Supabase.",
    category: "realtime",
    functions: [
      { name: "createChannel", signature: "(name: string) => RealtimeChannel", description: "Cria canal" },
      { name: "subscribeToTable", signature: "(table, callback) => Subscription", description: "Inscreve em tabela" },
      { name: "unsubscribeAll", signature: "() => void", description: "Cancela todas as inscrições" },
    ],
  },
  {
    name: "supabase-retry",
    path: "src/lib/supabase-retry.ts",
    description: "Retry automático para operações do Supabase.",
    category: "realtime",
    functions: [
      { name: "withRetry", signature: "(fn, options?) => Promise<T>", description: "Executa com retry" },
      { name: "isRetryableError", signature: "(error: Error) => boolean", description: "Verifica se é retryable" },
    ],
  },

  // ========== EXPORTAÇÃO ==========
  {
    name: "backup-export",
    path: "src/lib/backup-export.ts",
    description: "Exportação de backup completo do restaurante.",
    category: "export",
    functions: [
      { name: "exportBackup", signature: "(restaurantId: string) => Promise<Blob>", description: "Gera backup JSON" },
      { name: "downloadBackup", signature: "(blob: Blob, filename: string) => void", description: "Baixa arquivo" },
    ],
  },
  {
    name: "report-export",
    path: "src/lib/report-export.ts",
    description: "Exportação de relatórios em PDF/Excel.",
    category: "export",
    functions: [
      { name: "exportToPDF", signature: "(data, title, columns) => Promise<Blob>", description: "Gera PDF" },
      { name: "exportToExcel", signature: "(data, sheetName) => Promise<Blob>", description: "Gera Excel" },
      { name: "downloadFile", signature: "(blob, filename) => void", description: "Baixa arquivo" },
    ],
  },
  {
    name: "cmv-export",
    path: "src/lib/cmv-export.ts",
    description: "Exportação específica de relatório CMV.",
    category: "export",
    functions: [
      { name: "exportCMVReport", signature: "(data: CMVData[]) => Promise<Blob>", description: "Exporta CMV para Excel" },
    ],
  },
];

// Helper para buscar libs por categoria
export function getLibsByCategory(categoryId: string): LibDoc[] {
  return LIBS_DOCUMENTATION.filter(l => l.category === categoryId);
}

// Helper para buscar lib por nome
export function getLibByName(name: string): LibDoc | undefined {
  return LIBS_DOCUMENTATION.find(l => l.name === name);
}

// Total de funções documentadas
export function getTotalFunctionsCount(): number {
  return LIBS_DOCUMENTATION.reduce((acc, lib) => acc + lib.functions.length, 0);
}
